import { useEffect, useMemo, useRef, useState } from 'react'
import { BytecodeCompiler } from './bytecode-compiler.ts'
import { OpCode } from './bytecode.ts'
import type { BytecodeProgram } from './bytecode.ts'
import { useChannelModeStore, useDualModeCodeStore, useGlobalBpmStore, useSidebarTabStore, useTabsStore } from './store.ts'

const compiler = new BytecodeCompiler()

function renderProgram(program: { bytecode: Float64Array; sequences: any[] }, label: string) {
  if (!program || !program.bytecode) {
    return (
      <div style={{ color: '#888' }}>
        // {label} Bytecode (not available)
      </div>
    )
  }

  const instructions: JSX.Element[] = []
  let pc = 0
  let instrNum = 0

  while (pc < program.bytecode.length) {
    const opcode = program.bytecode[pc++]
    const argCount = program.bytecode[pc++]

    if (opcode === undefined || argCount === undefined) break

    const args: number[] = []
    for (let i = 0; i < argCount; i++) {
      args.push(program.bytecode[pc++])
    }

    instructions.push(
      <div key={instrNum}>
        {instrNum.toString().padStart(4, ' ')}:{' '}
        <span style={{ color: '#c586c0' }}>
          {OpCode[opcode] || opcode}
        </span>
        {argCount > 0 && (
          <span style={{ color: '#ce9178' }}>
            {' '}[{args.join(', ')}]
          </span>
        )}
      </div>,
    )
    instrNum++
  }

  return (
    <>
      <div style={{ color: '#4ec9b0' }}>
        // {label} Bytecode
      </div>
      {instructions}
      <div style={{ color: '#4ec9b0', marginTop: '10px' }}>
        // Sequences
      </div>
      {program.sequences.map((seq, i) => (
        <div key={i}>
          {i}: seq#{seq.id} ({seq.notes.length} notes)
        </div>
      ))}
    </>
  )
}

export function CompiledOutput() {
  const sidebarTab = useSidebarTabStore(state => state.sidebarTab)
  const channelMode = useChannelModeStore(state => state.channelMode)
  const activeTabId = useTabsStore(state => state.activeTabId)
  const getTabs = useTabsStore.getState
  const getGlobalBpmStore = useGlobalBpmStore.getState
  const dualCodeA = useDualModeCodeStore(state => state.codeA)
  const dualCodeB = useDualModeCodeStore(state => state.codeB)

  const [programA, setProgramA] = useState<BytecodeProgram | null>(null)
  const [programB, setProgramB] = useState<BytecodeProgram | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isCompiling, setIsCompiling] = useState(false)

  const prevActiveTabIdRef = useRef<string | null>(null)
  const prevCodeARef = useRef<string>('')
  const prevCodeBRef = useRef<string>('')

  // Only compile when the compiled tab is visible
  const isVisible = sidebarTab === 'compiled'

  // Compile code when tab changes or code changes (single mode)
  useEffect(() => {
    if (!isVisible) return
    if (channelMode !== 'single') return
    if (!activeTabId) return

    const tabs = getTabs().tabs
    const tab = tabs.find(t => t.id === activeTabId)
    if (!tab) return

    const codeA = tab.codeA
    const codeB = tab.codeB

    // Check if we need to recompile
    const tabChanged = prevActiveTabIdRef.current !== activeTabId
    const codeAChanged = prevCodeARef.current !== codeA
    const codeBChanged = prevCodeBRef.current !== codeB

    if (!tabChanged && !codeAChanged && !codeBChanged) {
      return
    }

    prevActiveTabIdRef.current = activeTabId
    prevCodeARef.current = codeA
    prevCodeBRef.current = codeB

    setIsCompiling(true)
    setError(null)

    try {
      const globalBpm = getGlobalBpmStore().globalBpm
      const program = compiler.compile(codeA, globalBpm)
      setProgramA(program)
      setProgramB(null)
      setError(null)
    }
    catch (err: any) {
      const errorMsg = err.message || 'Compilation error'
      setError(errorMsg)
      setProgramA(null)
      setProgramB(null)
    }
    finally {
      setIsCompiling(false)
    }
  }, [isVisible, activeTabId, channelMode, getTabs, getGlobalBpmStore])

  // Subscribe to tab changes to recompile when code changes (single mode)
  useEffect(() => {
    if (!isVisible) return
    if (channelMode !== 'single') return

    const unsubscribe = useTabsStore.subscribe(
      (state) => {
        const tab = state.tabs.find(t => t.id === state.activeTabId)
        return tab ? { codeA: tab.codeA, codeB: tab.codeB, activeTabId: tab.id } : null
      },
      (tabData) => {
        if (!tabData) return
        if (tabData.activeTabId !== activeTabId) return

        const codeAChanged = prevCodeARef.current !== tabData.codeA
        const codeBChanged = prevCodeBRef.current !== tabData.codeB

        if (!codeAChanged && !codeBChanged) return

        prevCodeARef.current = tabData.codeA
        prevCodeBRef.current = tabData.codeB

        setIsCompiling(true)
        setError(null)

        try {
          const globalBpm = getGlobalBpmStore().globalBpm
          const program = compiler.compile(tabData.codeA, globalBpm)
          setProgramA(program)
          setProgramB(null)
          setError(null)
        }
        catch (err: any) {
          const errorMsg = err.message || 'Compilation error'
          setError(errorMsg)
          setProgramA(null)
          setProgramB(null)
        }
        finally {
          setIsCompiling(false)
        }
      },
    )

    return unsubscribe
  }, [isVisible, activeTabId, channelMode, getGlobalBpmStore])

  // Dual mode: compile both channels from dual mode store
  useEffect(() => {
    if (!isVisible) return
    if (channelMode !== 'dual') return

    const codeA = dualCodeA || ''
    const codeB = dualCodeB || ''
    const hasA = !!codeA.trim()
    const hasB = !!codeB.trim()

    if (!hasA && !hasB) {
      setProgramA(null)
      setProgramB(null)
      setError(null)
      return
    }

    setIsCompiling(true)
    setError(null)

    try {
      const globalBpm = getGlobalBpmStore().globalBpm
      let compiledA: BytecodeProgram | null = null
      let compiledB: BytecodeProgram | null = null

      if (hasA) {
        compiledA = compiler.compile(codeA, globalBpm)
      }
      if (hasB) {
        compiledB = compiler.compile(codeB, globalBpm)
      }

      setProgramA(compiledA)
      setProgramB(compiledB)
    }
    catch (err: any) {
      const errorMsg = err.message || 'Compilation error'
      setError(errorMsg)
      setProgramA(null)
      setProgramB(null)
    }
    finally {
      setIsCompiling(false)
    }
  }, [isVisible, channelMode, dualCodeA, dualCodeB, getGlobalBpmStore])

  const handleClick = () => {
    const element = document.getElementById('compiledOutput')
    if (!element) return

    const textContent = element.textContent || element.innerText || ''
    if (textContent) {
      navigator.clipboard.writeText(textContent).catch(() => {})
    }
  }

  if (error) {
    return (
      <div
        className="flex-1 p-4 bg-black text-[#d4d4d4] font-mono text-xs leading-[1.5] overflow-auto cursor-pointer"
        id="compiledOutput"
        onClick={handleClick}
      >
        <div style={{ color: '#f48771' }}>
          // Error: {error}
        </div>
      </div>
    )
  }

  if (isCompiling) {
    return (
      <div
        className="flex-1 p-4 bg-black text-[#d4d4d4] font-mono text-xs leading-[1.5] overflow-auto cursor-pointer"
        id="compiledOutput"
        onClick={handleClick}
      >
        <div style={{ color: '#888' }}>
          // Compiling...
        </div>
      </div>
    )
  }

  if (!programA && !programB) {
    return (
      <div
        className="flex-1 p-4 bg-black text-[#d4d4d4] font-mono text-xs leading-[1.5] overflow-auto cursor-pointer"
        id="compiledOutput"
        onClick={handleClick}
      >
        <div style={{ color: '#888' }}>
          // No compiled program available
        </div>
      </div>
    )
  }

  const sizeA = programA?.bytecode?.length || 0
  const sizeB = programB?.bytecode?.length || 0

  return (
    <div
      className="flex-1 p-4 bg-black text-[#d4d4d4] font-mono text-xs leading-[1.5] overflow-auto cursor-pointer"
      id="compiledOutput"
      onClick={handleClick}
    >
      {renderProgram(programA, 'Channel A')}
      {programB && (
        <>
          <div style={{ marginTop: '20px', borderTop: '1px solid #444', paddingTop: '10px' }} />
          {renderProgram(programB, 'Channel B')}
        </>
      )}
      <div style={{ color: '#4ec9b0', marginTop: '10px' }}>
        // Stats
      </div>
      <div>
        A: {sizeA} floats ({(sizeA * 8 / 1024).toFixed(2)} KB)
      </div>
      {programB && programB.bytecode && (
        <div>
          B: {sizeB} floats ({(sizeB * 8 / 1024).toFixed(2)} KB)
        </div>
      )}
    </div>
  )
}
